import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import api from '../utils/api';

const columns = [
  { id: 'old_price', label: 'Ancien prix', align: 'right' },
  { id: 'new_price', label: 'Nouveau prix', align: 'right' },
  { id: 'price_variation', label: 'Variation', align: 'right' },
  { id: 'variation_type', label: 'Type', align: 'center' },
  { id: 'variation_percentage', label: 'Variation (%)', align: 'right' },
  { id: 'created_at', label: 'Date', align: 'center' },
  { id: 'reason', label: 'Raison', align: 'left' },
];

const getTypeIcon = (type) => {
  if (type === 'increase') {
    return <ArrowDropUpIcon sx={{ color: 'success.main', verticalAlign: 'middle' }} titleAccess="Hausse" />;
  }
  if (type === 'decrease') {
    return <ArrowDropDownIcon sx={{ color: 'error.main', verticalAlign: 'middle' }} titleAccess="Baisse" />;
  }
  return null;
};

const formatPrice = (val) =>
  typeof val === 'number' || (typeof val === 'string' && val !== '' && !isNaN(val))
    ? `${Number(val).toFixed(2)} €`
    : '-';

const formatVariation = (val) =>
  typeof val === 'number' || (typeof val === 'string' && val !== '' && !isNaN(val))
    ? `${Number(val) > 0 ? '+' : ''}${Number(val).toFixed(2)} €`
    : '-';

const formatPercent = (val) =>
  typeof val === 'number' || (typeof val === 'string' && val !== '' && !isNaN(val))
    ? `${Number(val) > 0 ? '+' : ''}${Number(val).toFixed(2)} %`
    : '-';

const RepricingHistoryModal = ({ open, onClose, repricingRulesId }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !repricingRulesId) return;
    setLoading(true);
    setError(null);
    setHistory([]);
    api.getRepricingHistory({ repricingRulesId })
      .then(data => {
        setHistory(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        setError('Erreur lors du chargement de l\'historique');
      })
      .finally(() => setLoading(false));
  }, [open, repricingRulesId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historique des changements de prix</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : history.length === 0 ? (
          <Typography>Aucun historique trouvé.</Typography>
        ) : (
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                {columns.map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontSize: 15 }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((row, idx) => (
                <TableRow key={row.id || idx} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <TableCell align="right">{formatPrice(row.old_price)}</TableCell>
                  <TableCell align="right">{formatPrice(row.new_price)}</TableCell>
                  <TableCell align="right">{formatVariation(row.price_variation)}</TableCell>
                  <TableCell align="center">
                    {getTypeIcon(row.variation_type)}
                    <span style={{ marginLeft: 4, fontWeight: 500, color: row.variation_type === 'increase' ? '#388e3c' : '#d32f2f' }}>
                      {row.variation_type === 'increase' ? 'Hausse' : row.variation_type === 'decrease' ? 'Baisse' : ''}
                    </span>
                  </TableCell>
                  <TableCell align="right">{formatPercent(row.variation_percentage)}</TableCell>
                  <TableCell align="center">{row.created_at ? new Date(row.created_at).toLocaleString('fr-FR') : '-'}</TableCell>
                  <TableCell align="left">{row.reason || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepricingHistoryModal; 